'use client';

import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/apiClient';

const AVAILABLE_CATEGORIES = [
    { id: 1, name: 'Painting' },
    { id: 2, name: 'Plumbing' },
    { id: 3, name: 'Electrical' },
    { id: 4, name: 'Carpentry' },
    { id: 5, name: 'Interior Design' },
    { id: 6, name: 'Renovation' },
    { id: 7, name: 'Cleaning Services' }
];

export default function BecomeCivilSupplierPage() {
    const router = useRouter();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1
        category_ids: [],
        supplier_name: '',
        phone: '',
        website: '',
        street_address: '',
        city_id: '',
        locality_id: '',
        sub_locality_id: '',
        product_list: '',
        // Step 2
        contact_name: '',
        experience: '',
        role: '',
        mobile: '',
        email: ''
    });

    const [errors, setErrors] = useState({});

    const [cities, setCities] = useState([]);
    const [localities, setLocalities] = useState([]);
    const [subLocalities, setSubLocalities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingLocalities, setLoadingLocalities] = useState([]);
    const [loadingSubLocalities, setLoadingSubLocalities] = useState([]);
    const [localityNotAvailable, setLocalityNotAvailable] = useState(false);
    const [subLocalityNotAvailable, setSubLocalityNotAvailable] = useState(false);
    const [loading, setLoading] = useState(false);
    const hasMounted = useRef(false);

    useEffect(() => {
        if (hasMounted.current) return;
        hasMounted.current = true;
        fetchValues();
    }, []);

    const fetchValues = async () => {
        try {
            setLoading(true);
            const [citiesRes, localitiesRes, subLocalitiesRes, categoriesRes] = await Promise.all([
                api.post('selectwithout/master_cities', { sqlData: "id,city_name", where: "is_active=$1", whereData: [true] }, { skipAuthGuard: true }),
                api.post('selectwithout/master_localities', { sqlData: "id,city_id,locality_name", where: "is_active=$1", whereData: [true] }, { skipAuthGuard: true }),
                api.post('selectwithout/master_sub_localities', { sqlData: "id,locality_id,sub_locality_name", where: "is_active=$1", whereData: [true] }, { skipAuthGuard: true }),
                api.post('selectwithout/master_product_categories', { sqlData: "id,product_category_name", where: "is_active=$1", whereData: [true] }, { skipAuthGuard: true }),
            ]);
            if (localitiesRes.success === 1 && Array.isArray(localitiesRes.data)) {
                setLocalities(localitiesRes.data);
            }
            if (citiesRes.success === 1 && Array.isArray(citiesRes.data)) {
                setCities(citiesRes.data);
            }
            if (subLocalitiesRes.success === 1 && Array.isArray(subLocalitiesRes.data)) {
                setSubLocalities(subLocalitiesRes.data);
            }
            if (categoriesRes.success === 1 && Array.isArray(categoriesRes.data)) {
                setCategories(categoriesRes.data.map(c => ({ id: c.id, name: c.product_category_name })));
            }
        } catch (error) {
            console.error('Error fetching values:', error);
        } finally {
            setLoading(false);
        }
    };

    const [categorySearch, setCategorySearch] = useState('');
    const [showCategoryList, setShowCategoryList] = useState(false);
    const [categoryIndex, setCategoryIndex] = useState(-1);

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(categorySearch.toLowerCase()) &&
        !formData.category_ids.some(selected => selected.id === c.id)
    );

    const handleCategorySelect = (category) => {
        setFormData(prev => ({
            ...prev,
            category_ids: [...prev.category_ids, category]
        }));
        setCategorySearch('');
        setShowCategoryList(false);
        if (errors.category_ids) {
            setErrors(prev => ({ ...prev, category_ids: '' }));
        }
    };

    const removeCategory = (id) => {
        setFormData(prev => ({
            ...prev,
            category_ids: prev.category_ids.filter(c => c.id !== id)
        }));
    };

    const handleCategoryKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCategoryIndex(prev => Math.min(prev + 1, filteredCategories.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCategoryIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (categoryIndex >= 0 && categoryIndex < filteredCategories.length) {
                handleCategorySelect(filteredCategories[categoryIndex]);
            }
        } else if (e.key === 'Backspace' && categorySearch === '' && formData.category_ids.length > 0) {
            removeCategory(formData.category_ids[formData.category_ids.length - 1].id);
        }
    };

    // Validate Step 1
    const validateStep1 = () => {
        const newErrors = {};
        if (formData.category_ids.length === 0) newErrors.category_ids = 'Category is required';
        if (!formData.supplier_name.trim()) newErrors.supplier_name = 'Company Name is required';
        if (!formData.phone.trim()) {
            newErrors.phone = 'Company Phone is required';
        } else if (!/^\d+$/.test(formData.phone)) {
            newErrors.phone = 'Phone must contain only numbers';
        }
        if (!formData.street_address.trim()) newErrors.street_address = 'Address is required';
        if (!formData.city_id) newErrors.city_id = 'Please select a city.';
        if (!formData.locality_id) newErrors.locality_id = 'Please select a locality.';
        if (!formData.sub_locality_id) newErrors.sub_locality_id = 'Please select a sub locality.';

        setErrors(newErrors);

        const errorKeys = Object.keys(newErrors);
        if (errorKeys.length > 0) {
            const firstErrorField = document.querySelector(`[name="${errorKeys[0]}"]`);
            if (firstErrorField) {
                firstErrorField.focus();
            }
            return false;
        }
        return true;
    };

    // Validate Step 2
    const validateStep2 = () => {
        const newErrors = {};
        if (!formData.contact_name.trim()) newErrors.contact_name = 'Contact Person is required';
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else if (!/^\d+$/.test(formData.mobile)) {
            newErrors.mobile = 'Mobile number must contain only numbers';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);

        const errorKeys = Object.keys(newErrors);
        if (errorKeys.length > 0) {
            const firstErrorField = document.querySelector(`[name="${errorKeys[0]}"]`);
            if (firstErrorField) {
                firstErrorField.focus();
            }
            return false;
        }
        return true;
    };

    const handleContinue = (e) => {
        e.preventDefault();
        if (validateStep1()) {
            setCurrentStep(2);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = (e) => {
        e.preventDefault();
        setCurrentStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateStep2()) {
            console.log('Form Submitted successfully:', formData);

            // Handle new locality if user typed a custom name
            var locality = 0;
            var subLocality = 0;
            if (localityNotAvailable) {
                const responseLocality = await api.post('insertwithout/master_localities', {
                    city_id: formData.city_id,
                    locality_name: formData.locality_id,
                    slug: formData.locality_id.toLowerCase().replace(/\s+/g, '-'),
                });
                if (responseLocality.success === 1) {
                    locality = responseLocality.insId;
                }
            } else {
                locality = parseInt(formData.locality_id);
            }

            // Handle new sub-locality if user typed a custom name
            if (subLocalityNotAvailable) {
                const responseSubLocality = await api.post('insertwithout/master_sub_localities', {
                    locality_id: locality,
                    sub_locality_name: formData.sub_locality_id,
                    slug: formData.sub_locality_id.toLowerCase().replace(/\s+/g, '-'),
                });
                if (responseSubLocality.success === 1) {
                    subLocality = responseSubLocality.insId;
                }
            } else {
                subLocality = parseInt(formData.sub_locality_id);
            }

            const address = {
                street_address: formData.street_address,
                city_id: formData.city_id,
                locality_id: locality,
                sub_locality_id: subLocality,
            }

            var addressId = null;
            const responseAddress = await api.post('insertwithout/addresses', address);
            if (responseAddress.success === 1) {
                addressId = responseAddress.insId;
            }

            const payload = {
                supplier_name: formData.supplier_name,
                phone: formData.phone,
                experience: formData.experience,
                website: formData.website,
                address_id: addressId,
                product_list: formData.product_list,
                contact_name: formData.contact_name,
                role: formData.role,
                mobile: formData.mobile,
                email: formData.email,
                category_ids: JSON.stringify(formData.category_ids.map(c => c.id))
            };

            const response = await api.post('insertwithout/suppliers', payload);
            if (response.success === 1) {
                alert('Your registration has been submitted successfully!');
                router.push('/');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Dependent dropdowns
        if (name === 'city_id') {
            setLoadingLocalities(localities.filter(l => l.city_id === value));
            setLocalityNotAvailable(false);
            setSubLocalityNotAvailable(false);
            setFormData(prev => ({
                ...prev,
                city_id: value,
                locality_id: '',
                sub_locality_id: ''
            }));
            return;
        }
        if (name === 'locality_id' && !localityNotAvailable) {
            if (value === 0) {
                setLocalityNotAvailable(true);
                setSubLocalityNotAvailable(false);
                setLoadingSubLocalities([]);
                setFormData(prev => ({ ...prev, locality_id: '', sub_locality_id: '' }));
            } else {
                setLoadingSubLocalities(subLocalities.filter(s => s.locality_id === value));
                setFormData(prev => ({ ...prev, locality_id: value, sub_locality_id: '' }));
            }
            return;
        }
        if (name === 'sub_locality_id' && !subLocalityNotAvailable) {
            if (value === 0) {
                setSubLocalityNotAvailable(true);
                setFormData(prev => ({ ...prev, sub_locality_id: '' }));
            } else {
                setFormData(prev => ({ ...prev, sub_locality_id: value }));
            }
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const getErrorClass = (fieldName) => {
        return errors[fieldName]
            ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-200 focus:border-red-500 hover:border-red-500'
            : 'border-gray-300 bg-white focus:ring-primary focus:border-primary hover:border-gray-400';
    };

    return (
        <>
            <Navbar />
            <div className="bg-gray-50 min-h-screen py-12">
                <div className="flex justify-center">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 w-full max-w-xl">

                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a Civil Supplier</h1>
                            <p className="text-gray-600">Join our platform to grow your business and connect with customers.</p>

                            {/* Step Indicator */}
                            <div className="flex items-center mt-6">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'} font-semibold text-sm`}>
                                    1
                                </div>
                                <div className={`flex-1 h-1 mx-2 ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-200'} rounded`}></div>
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'} font-semibold text-sm`}>
                                    2
                                </div>
                            </div>
                            <div className="flex justify-between mt-2 text-xs font-medium text-gray-500">
                                <span>Company Details</span>
                                <span>Contact Details</span>
                            </div>
                        </div>

                        <form onSubmit={currentStep === 1 ? handleContinue : handleSubmit} className="space-y-6">

                            {currentStep === 1 && (
                                <>
                                    {/* 1. Category */}
                                    <div className="flex flex-col relative">
                                        {/*This code is for new category selection*/}
                                        <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                            Category*
                                            {errors.categories && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                        </label>
                                        <div className={`input flex flex-wrap items-center gap-1.5 transition-colors duration-200 py-1.5 min-h-[46px] ${getErrorClass('categories')}`}>
                                            {formData.category_ids.map((c) => (
                                                <div key={c.id} className="flex items-center gap-1 bg-blue-50/50 border border-blue-100 text-blue-700 px-2 py-0.5 rounded text-[13px] whitespace-nowrap">
                                                    <span>{c.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCategory(c.id)}
                                                        className="hover:text-blue-900 focus:outline-none"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                            <input
                                                name="categories"
                                                className="flex-1 min-w-[100px] text-sm text-gray-800 placeholder-gray-400 focus:outline-none bg-transparent py-1"
                                                placeholder={formData.category_ids.length === 0 ? "Search by Category..." : ""}
                                                value={categorySearch}
                                                onFocus={() => setShowCategoryList(true)}
                                                onChange={(e) => {
                                                    setCategorySearch(e.target.value);
                                                    setShowCategoryList(true);
                                                    setCategoryIndex(-1);
                                                }}
                                                onBlur={() => {
                                                    // Small delay to allow click event to register before hiding
                                                    setTimeout(() => setShowCategoryList(false), 200);
                                                }}
                                                autoComplete="off"
                                                onKeyDown={handleCategoryKeyDown}
                                            />
                                        </div>
                                        {showCategoryList && filteredCategories.length > 0 && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg rounded-lg z-50 max-h-48 overflow-y-auto">
                                                {filteredCategories.map((c, idx) => (
                                                    <div
                                                        key={c.id}
                                                        className={`px-4 py-2 cursor-pointer text-sm ${idx === categoryIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault(); // Prevents blur on input
                                                            handleCategorySelect(c);
                                                        }}
                                                    >
                                                        {c.name}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. Company Name */}
                                    <div className="flex flex-col">
                                        <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                            Company Name*
                                            {errors.supplier_name && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                        </label>
                                        <input
                                            type="text"
                                            name="supplier_name"
                                            value={formData.supplier_name}
                                            onChange={handleChange}
                                            placeholder="Your Company or Business Name"
                                            className={`input transition-colors duration-200 ${getErrorClass('supplier_name')}`}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* 3. Company Phone */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Company Phone*
                                                {errors.phone && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Company Phone"
                                                className={`input transition-colors duration-200 ${getErrorClass('phone')}`}
                                            />
                                        </div>

                                        {/* 4. Website */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Website
                                                {errors.website && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <input
                                                type="text"
                                                name="website"
                                                value={formData.website}
                                                onChange={handleChange}
                                                placeholder="https://yourwebsite.com"
                                                className={`input transition-colors duration-200 ${getErrorClass('website')}`}
                                            />
                                        </div>
                                    </div>

                                    {/* 5. Experience */}
                                    <div className="flex flex-col">
                                        <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                            Experience*
                                            {errors.experience && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                        </label>
                                        <input
                                            type="text"
                                            name="experience"
                                            value={formData.experience}
                                            onChange={handleChange}
                                            placeholder="10+ Years"
                                            className={`input transition-colors duration-200 ${getErrorClass('experience')}`}
                                        />
                                    </div>

                                    {/* 5. Address */}
                                    <div className="flex flex-col">
                                        <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                            Address*
                                            {errors.street_address && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                        </label>
                                        <input
                                            type="text"
                                            name="street_address"
                                            value={formData.street_address}
                                            onChange={handleChange}
                                            placeholder="Full street address..."
                                            className={`input transition-colors duration-200 ${getErrorClass('street_address')}`}
                                        />
                                    </div>

                                    {/* 6. City, Locality, Sub Locality */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <SearchableDropdown
                                            label="City*"
                                            name="city_id"
                                            value={formData.city_id}
                                            options={cities.map(c => ({ label: c.city_name, value: c.id }))}
                                            onChange={handleChange}
                                            placeholder="Select City"
                                            required
                                            strict={true}
                                            error={errors.city_id}
                                        />

                                        {/* Locality */}
                                        {localityNotAvailable ? (
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                    <span>Locality* <span className="text-xs text-gray-400 font-normal">(new)</span></span>
                                                    {errors.locality_id && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                                </label>
                                                <div className="flex gap-2 items-start">
                                                    <input
                                                        type="text"
                                                        name="locality_id"
                                                        value={formData.locality_id}
                                                        onChange={handleChange}
                                                        placeholder="Type locality name"
                                                        className={`input flex-1 ${errors.locality_id ? 'border-red-500 bg-red-50 text-red-900 focus:border-red-500' : 'border-gray-300 bg-white focus:border-primary focus:ring-primary'}`}
                                                        required
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setLocalityNotAvailable(false);
                                                            setFormData(prev => ({ ...prev, locality_id: '', sub_locality_id: '' }));
                                                            setSubLocalityNotAvailable(false);
                                                        }}
                                                        className="mt-1 text-xs text-gray-500 underline whitespace-nowrap"
                                                    >
                                                        ← Back to list
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <SearchableDropdown
                                                label="Locality*"
                                                name="locality_id"
                                                value={formData.locality_id}
                                                options={[
                                                    { label: '+ Not available (add new)', value: 0 },
                                                    ...loadingLocalities.map(l => ({ label: l.locality_name, value: l.id })),
                                                ]}
                                                onChange={handleChange}
                                                placeholder="Search Locality"
                                                required
                                                strict={true}
                                                error={errors.locality_id}
                                            />
                                        )}
                                    </div>



                                    {/* Sub Locality */}
                                    {subLocalityNotAvailable ? (
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                <span>Sub Locality* <span className="text-xs text-gray-400 font-normal">(new)</span></span>
                                                {errors.sub_locality_id && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <div className="flex gap-2 items-start">
                                                <input
                                                    type="text"
                                                    name="sub_locality_id"
                                                    value={formData.sub_locality_id}
                                                    onChange={handleChange}
                                                    placeholder="Type sub locality name"
                                                    className={`input flex-1 ${errors.sub_locality_id ? 'border-red-500 bg-red-50 text-red-900 focus:border-red-500' : 'border-gray-300 bg-white focus:border-primary focus:ring-primary'}`}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSubLocalityNotAvailable(false);
                                                        setFormData(prev => ({ ...prev, sub_locality_id: '' }));
                                                    }}
                                                    className="mt-1 text-xs text-gray-500 underline whitespace-nowrap"
                                                >
                                                    ← Back to list
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <SearchableDropdown
                                            label="Sub Locality*"
                                            name="sub_locality_id"
                                            value={formData.sub_locality_id}
                                            options={[
                                                { label: '+ Not available (add new)', value: 0 },
                                                ...loadingSubLocalities.map(s => ({ label: s.sub_locality_name, value: s.id })),
                                            ]}
                                            onChange={handleChange}
                                            placeholder="Search Sub Locality"
                                            strict={true}
                                            error={errors.sub_locality_id}
                                        />
                                    )}

                                    {/* 6. Products List */}
                                    <div className="flex flex-col">
                                        <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                            Products List
                                            {errors.product_list && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                        </label>
                                        <textarea
                                            name="product_list"
                                            value={formData.product_list}
                                            onChange={handleChange}
                                            placeholder="List of products you supply..."
                                            rows="2"
                                            className={`input resize-y transition-colors duration-200 py-3 ${getErrorClass('product_list')}`}
                                        ></textarea>
                                    </div>

                                    {/* Continue Button */}
                                    <div className="pt-6 border-t border-gray-100 flex justify-end">
                                        <button type="submit" className="btn btn-primary px-10 py-3 text-base shadow-md">
                                            Continue
                                        </button>
                                    </div>
                                </>
                            )}

                            {currentStep === 2 && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Contact Person */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Contact Person*
                                                {errors.contact_name && <p className="text-sm font-medium text-red-500 mt-1"> Must* </p>}
                                            </label>
                                            <input
                                                type="text"
                                                name="contact_name"
                                                value={formData.contact_name}
                                                onChange={handleChange}
                                                placeholder="Full Name"
                                                className={`input transition-colors duration-200 ${getErrorClass('contact_name')}`}
                                            />
                                        </div>

                                        {/* Role */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Role
                                                {errors.role && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <input
                                                type="text"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                placeholder="e.g. Manager, Owner"
                                                className={`input transition-colors duration-200 ${getErrorClass('role')}`}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Mobile Number */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Mobile Number*
                                                {errors.mobile && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                placeholder="Mobile Number"
                                                className={`input transition-colors duration-200 ${getErrorClass('mobile')}`}
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="flex flex-col">
                                            <label className="text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                                Email
                                                {errors.email && <p className="text-sm font-medium text-red-500 mt-1">Must*</p>}
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="example@email.com"
                                                className={`input transition-colors duration-200 ${getErrorClass('email')}`}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="pt-6 border-t border-gray-100 flex justify-between">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="px-6 py-3 text-base font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            Back
                                        </button>
                                        <button type="submit" className="btn btn-primary px-10 py-3 text-base shadow-md">
                                            Submit Registration
                                        </button>
                                    </div>
                                </>
                            )}

                        </form>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}

